import React from "react";
import { useTranslation } from "react-i18next";
import { Download, Pencil, Trash2, Users } from "lucide-react";
import ReportButton from "../messages/ReportButton";
import { Material } from "../../features/materials/models/Material";
import { MaterialUploader } from "../../features/materials/contracts/MaterialUploader";
import { formatFileSize } from "../../features/materials/utils/formatFileSize";

interface MaterialListProps {
  materials: Material[];
  memberNameById: Map<string, string>;
  groupNameById: Map<string, string>;
  onDownload: (material: Material) => void;
  onEdit: (material: Material) => void;
  onDelete: (material: Material) => void;
}

/** The organization's materials as rows with download / edit / delete actions. */
const MaterialList: React.FC<MaterialListProps> = ({
  materials,
  memberNameById,
  groupNameById,
  onDownload,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  if (materials.length === 0) {
    return (
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {t("materials.empty", "No materials yet — upload the first file.")}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {materials.map((material) => {
        const uploader =
          material.uploadedBy === MaterialUploader.Member && material.uploadedByMemberId
            ? memberNameById.get(material.uploadedByMemberId) ??
              t("materials.uploaderMember", "Member")
            : t("materials.uploaderInstructor", "Instructor");
        const audienceNames = [
          ...material.groupIds.map((id) => groupNameById.get(id)).filter(Boolean),
          ...material.memberIds.map((id) => memberNameById.get(id)).filter(Boolean),
        ] as string[];
        return (
          <li
            key={material.id}
            className="with-border flex items-center gap-3 rounded-2xl bg-white p-3 dark:bg-primary-10"
          >
            <div className="min-w-0 flex-1">
              <button
                type="button"
                className="block max-w-full truncate text-left text-sm font-medium text-grey-20 cursor-pointer hover:underline dark:text-white"
                onClick={() => onDownload(material)}
              >
                {material.fileName}
              </button>
              <p className="truncate text-sm text-grey-40 dark:text-grey-80">
                {material.category} · {formatFileSize(material.sizeBytes)} · {uploader} ·{" "}
                {dateFormat.format(material.dateCreated)}
                {material.description ? ` · ${material.description}` : ""}
              </p>
              {audienceNames.length > 0 && (
                <p className="flex items-center gap-1 truncate text-xs text-primary-40 dark:text-primary-90">
                  <Users size={12} className="shrink-0" />
                  {t("materials.sharedWith", "Shared with")} {audienceNames.join(", ")}
                </p>
              )}
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onDownload(material)}
              aria-label={t("materials.downloadAction", "Download")}
            >
              <Download size={16} />
            </button>
            {material.uploadedBy === MaterialUploader.Member && (
              <ReportButton face="staff" target={{ targetKind: "Material", targetId: material.id }} />
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onEdit(material)}
              aria-label={t("materials.editTitle", "Edit material")}
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onDelete(material)}
              aria-label={t("materials.deleteAction", "Delete material")}
            >
              <Trash2 size={16} />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default MaterialList;
